// D1 Cotton 실측 데이터 파서
// docs/sample/8.12 cotton (D1~D5)/{TIC1.csv, MS1.csv} → Vite ?raw import
// 런타임 dependency 없이 inline parser 사용

import msCsv from "@/assets/real-data/d1-cotton-ms.csv?raw";
import ticCsv from "@/assets/real-data/d1-cotton-tic.csv?raw";
import type { MassSpectrumData, TICData } from "@/types/spectrum";

// ─── TIC Parser ───────────────────────────────────────────────────────────────
// TIC1.csv format:
//   #"+EI TIC Scan diesel_gas_1.D "
//   #Point,X(Minutes),Y(Counts)
//   0,0.095,0
//   ...

export const d1CottonTIC: TICData[] = (() => {
  const lines = ticCsv.split("\n");
  const data: TICData[] = [];
  for (const line of lines) {
    if (line.startsWith("#") || line.trim() === "") continue;
    const parts = line.split(",");
    if (parts.length < 3) continue;
    const rt = parseFloat(parts[1].trim());
    const intensity = parseFloat(parts[2].trim());
    if (!Number.isNaN(rt) && !Number.isNaN(intensity)) {
      data.push({ rt, intensity });
    }
  }
  return data;
})();

// ─── MS Scan type ─────────────────────────────────────────────────────────────

export interface MSScan {
  /** 실측 RT — vendor scan trigger 시점 (분) */
  rt: number;
  /** m/z + intensity pairs (원본 Y(Counts) 단위) */
  data: MassSpectrumData[];
}

// ─── MS Parser ────────────────────────────────────────────────────────────────
// MS1.csv format:
//   #"+EI Scan (rt: 1.222 min) diesel_gas_1.D "
//   #Point,X(Thomsons),Y(Counts)
//   0,30.0,1795.43273925781
//   ...

export const d1CottonMSScans: MSScan[] = (() => {
  const scans: MSScan[] = [];
  const lines = msCsv.split("\n");
  let currentRT: number | null = null;
  let currentData: MassSpectrumData[] = [];

  for (const line of lines) {
    const rtMatch = line.match(/EI Scan \(rt: ([\d.]+) min\)/);
    if (rtMatch) {
      // Flush previous scan
      if (currentRT !== null && currentData.length > 0) {
        scans.push({ rt: currentRT, data: currentData });
      }
      currentRT = parseFloat(rtMatch[1]);
      currentData = [];
    } else if (line.startsWith("#") || line.trim() === "") {
    } else if (currentRT !== null) {
      const parts = line.split(",");
      if (parts.length < 3) continue;
      const mz = parseFloat(parts[1].trim());
      const intensity = parseFloat(parts[2].trim());
      if (!Number.isNaN(mz) && !Number.isNaN(intensity)) {
        currentData.push({ mz, intensity });
      }
    }
  }
  // Flush last scan
  if (currentRT !== null && currentData.length > 0) {
    scans.push({ rt: currentRT, data: currentData });
  }
  return scans;
})();

// ─── Helper: nearest scan ─────────────────────────────────────────────────────

/**
 * 클릭 RT에 가장 가까운 실측 MS 스캔을 반환한다.
 * TIC 2410 포인트 중 임의 RT 클릭 → 16개 실측 스캔 중 nearest 반환.
 */
export function getNearestScan(rt: number): MSScan | null {
  if (d1CottonMSScans.length === 0) return null;
  return d1CottonMSScans.reduce((prev, cur) =>
    Math.abs(cur.rt - rt) < Math.abs(prev.rt - rt) ? cur : prev,
  );
}
